/**
 * Zustand store for BCR management.
 */

import { create } from 'zustand';
import * as bcrApi from '@/api/bcr';
import type {
  BcrTaskDetail,
  BcrTaskCreate,
  BcrStatusBrief,
  BcrStatsSummary,
  BcrTimelineItem,
  BcrStageFailure,
  BcrFailureReason,
} from '@/types';

interface BcrState {
  // Current task (BCR test page)
  currentTask: BcrTaskDetail | null;
  isCreating: boolean;
  isLoading: boolean;
  isCancelling: boolean;
  isRerunning: boolean;
  error: string | null;

  // Polling
  _pollTimer: ReturnType<typeof setInterval> | null;

  // Log state
  logs: string[];
  logOffset: number;
  isLogPanelCollapsed: boolean;

  // Batch status for package list
  bcrStatusMap: Record<string, BcrStatusBrief>;

  // BCR Dashboard stats
  statsSummary: BcrStatsSummary | null;
  statsTimeline: BcrTimelineItem[];
  statsStageFailures: BcrStageFailure[];
  statsFailureReasons: BcrFailureReason[];
  isStatsLoading: boolean;
  statsError: string | null;

  // Actions
  createTask: (projectId: number, data: BcrTaskCreate) => Promise<BcrTaskDetail | null>;
  fetchTaskDetail: (projectId: number, taskId: number) => Promise<void>;
  refreshTaskDetail: (projectId: number, taskId: number) => Promise<void>;
  cancelTask: (projectId: number, taskId: number) => Promise<boolean>;
  rerunSubTask: (projectId: number, taskId: number, subTaskName: string) => Promise<boolean>;
  fetchBatchBcrStatus: (packageIds: number[]) => Promise<void>;
  fetchStats: (params?: {
    project_id?: number;
    granularity?: 'week' | 'month';
    start_date?: string;
    end_date?: string;
  }) => Promise<void>;
  startPolling: (projectId: number, taskId: number) => void;
  stopPolling: () => void;
  setLogPanelCollapsed: (collapsed: boolean) => void;
  resetLogs: () => void;
  clearError: () => void;
  reset: () => void;
}

export const useBcrStore = create<BcrState>()((set, get) => ({
  currentTask: null,
  isCreating: false,
  isLoading: false,
  isCancelling: false,
  isRerunning: false,
  error: null,
  _pollTimer: null,
  logs: [],
  logOffset: 0,
  isLogPanelCollapsed: false,
  bcrStatusMap: {},

  // BCR Dashboard stats
  statsSummary: null,
  statsTimeline: [],
  statsStageFailures: [],
  statsFailureReasons: [],
  isStatsLoading: false,
  statsError: null,

  createTask: async (projectId, data) => {
    set({ isCreating: true, error: null });
    try {
      const task = await bcrApi.createBcrTask(projectId, data);
      set({ currentTask: task, isCreating: false, logs: [], logOffset: 0 });
      return task;
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string }; status?: number } };
      const message = err?.response?.data?.message || 'BCR任务创建失败';
      set({ error: message, isCreating: false });
      return null;
    }
  },

  fetchTaskDetail: async (projectId, taskId) => {
    set({ isLoading: true, error: null });
    try {
      const { logOffset } = get();
      const task = await bcrApi.getBcrTaskDetail(projectId, taskId, logOffset);
      const newLogs = task.logs || [];
      set((state) => ({
        currentTask: task,
        isLoading: false,
        logs: [...state.logs, ...newLogs],
        logOffset: state.logOffset + newLogs.length,
      }));
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      const message = err?.response?.data?.message || '获取BCR任务详情失败';
      set({ error: message, isLoading: false });
    }
  },

  cancelTask: async (projectId, taskId) => {
    set({ isCancelling: true, error: null });
    try {
      const task = await bcrApi.cancelBcrTask(projectId, taskId);
      set({ currentTask: task, isCancelling: false });
      get().stopPolling();
      return true;
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      const message = err?.response?.data?.message || 'BCR任务终止失败';
      set({ error: message, isCancelling: false });
      return false;
    }
  },

  rerunSubTask: async (projectId, taskId, subTaskName) => {
    set({ isRerunning: true, error: null });
    try {
      const task = await bcrApi.rerunBcrSubTask(projectId, taskId, subTaskName);
      set({ currentTask: task, isRerunning: false, logs: [], logOffset: 0 });
      get().startPolling(projectId, taskId);
      return true;
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      const message = err?.response?.data?.message || 'BCR子任务重跑失败';
      set({ error: message, isRerunning: false });
      return false;
    }
  },

  fetchBatchBcrStatus: async (packageIds) => {
    if (!packageIds.length) return;
    try {
      const result = await bcrApi.getBatchBcrStatus(packageIds);
      set({ bcrStatusMap: result });
    } catch {
      // Silently fail — button will show default state
    }
  },

  fetchStats: async (params) => {
    set({ isStatsLoading: true, statsError: null });
    try {
      const result = await bcrApi.getBcrStats(params);
      set({
        statsSummary: result.summary,
        statsTimeline: result.timeline,
        statsStageFailures: result.stage_failures,
        statsFailureReasons: result.failure_reasons,
        isStatsLoading: false,
      });
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      const message = err?.response?.data?.message || '获取BCR统计数据失败';
      set({ statsError: message, isStatsLoading: false });
    }
  },

  startPolling: (projectId, taskId) => {
    const state = get();
    // Clear existing timer
    if (state._pollTimer) {
      clearInterval(state._pollTimer);
    }

    // Execute first poll immediately, then start interval
    (async () => {
      const current = get().currentTask;
      if (!current || current.id !== taskId) return;
      try {
        const { logOffset } = get();
        const task = await bcrApi.getBcrTaskDetail(projectId, taskId, logOffset);
        if (get().currentTask?.id !== taskId) return;
        const newLogs = task.logs || [];
        set((state) => ({
          currentTask: task,
          logs: [...state.logs, ...newLogs],
          logOffset: state.logOffset + newLogs.length,
        }));
        if (task.status !== 'running') {
          get().stopPolling();
          return;
        }
      } catch {
        // Silent fail
      }
    })();

    const timer = setInterval(async () => {
      const current = get().currentTask;
      // Stop polling if task is no longer running or switched to a different task
      if (!current || current.id !== taskId || current.status !== 'running') {
        get().stopPolling();
        return;
      }
      try {
        const { logOffset } = get();
        const task = await bcrApi.getBcrTaskDetail(projectId, taskId, logOffset);
        // Guard: discard if user switched to a different task during the request
        if (get().currentTask?.id !== taskId) return;
        const newLogs = task.logs || [];
        set((state) => ({
          currentTask: task,
          logs: [...state.logs, ...newLogs],
          logOffset: state.logOffset + newLogs.length,
        }));
        // Stop polling when task reaches terminal state (finished, stop)
        if (task.status === 'finished' || task.status === 'stop') {
          // Do a final refresh to ensure we have all logs
          await get().refreshTaskDetail(projectId, taskId);
          get().stopPolling();
        }
      } catch {
        // Silent retry on next poll
      }
    }, 30000);
    set({ _pollTimer: timer });
  },

  stopPolling: () => {
    const timer = get()._pollTimer;
    if (timer) {
      clearInterval(timer);
      set({ _pollTimer: null });
    }
  },

  // Force refresh task detail (for when polling stops)
  refreshTaskDetail: async (projectId, taskId) => {
    try {
      const task = await bcrApi.getBcrTaskDetail(projectId, taskId, 0);
      set({
        currentTask: task,
        logs: task.logs || [],
        logOffset: (task.logs || []).length,
      });
    } catch {
      // Silent fail
    }
  },

  setLogPanelCollapsed: (collapsed) => set({ isLogPanelCollapsed: collapsed }),

  resetLogs: () => set({ logs: [], logOffset: 0 }),

  clearError: () => set({ error: null }),

  reset: () => {
    get().stopPolling();
    set({
      currentTask: null,
      isCreating: false,
      isLoading: false,
      isCancelling: false,
      isRerunning: false,
      error: null,
      _pollTimer: null,
      logs: [],
      logOffset: 0,
      isLogPanelCollapsed: false,
      statsSummary: null,
      statsTimeline: [],
      statsStageFailures: [],
      statsFailureReasons: [],
      isStatsLoading: false,
      statsError: null,
    });
  },
}));
